import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Input, { Select, Label } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import { formatDateShort, getDayOfWeek, getToday } from '../../lib/dateUtils';
import { Save, Plus, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    standardExitTime: '17:00',
    onDutyPushEnabled: false,
    onDutyPushTime: '08:00',
    autoPushEnabled: false,
    autoPushTime: '17:30',
    overtimePushEnabled: false,
  });
  const [saved, setSaved] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidayForm, setHolidayForm] = useState({ date: '', name: '', type: 'holiday' });
  const [holidayError, setHolidayError] = useState(null);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    select: (res) => res.data,
  });

  const { data: holidayList, isLoading: holidaysLoading } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => settingsApi.getHolidays(year),
    select: (res) => res.data,
  });

  useEffect(() => {
    if (settings) {
      setForm({
        standardExitTime: settings.standardExitTime,
        onDutyPushEnabled: settings.onDutyPushEnabled,
        onDutyPushTime: settings.onDutyPushTime,
        autoPushEnabled: settings.autoPushEnabled,
        autoPushTime: settings.autoPushTime,
        overtimePushEnabled: settings.overtimePushEnabled,
      });
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data) => settingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      queryClient.invalidateQueries(['reports']);
      setSaved(true);
    },
  });

  const holidayMutation = useMutation({
    mutationFn: (data) => settingsApi.saveHoliday(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['holidays']);
      queryClient.invalidateQueries(['reports']);
      setHolidayForm({ date: '', name: '', type: 'holiday' });
      setHolidayError(null);
    },
    onError: (error) => setHolidayError(error.response?.data?.error || t('errors.generic', '操作失敗')),
  });

  const deleteHolidayMutation = useMutation({
    mutationFn: (id) => settingsApi.deleteHoliday(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['holidays']);
      queryClient.invalidateQueries(['reports']);
    },
  });

  // Today's 加班結束推播 outlook, mirroring the scheduler's rules
  const overtimeStatus = () => {
    const latest = settings?.todayLatestOvertimeExit;
    if (!latest) return t('settings.overtimePushNone', '今日目前無加班工項');
    const base = t('settings.overtimeLatest', '今日最晚加班退場時間：{{time}}', { time: latest });
    const last = settings.overtimePushLast;
    if (last && last.date === getToday() && last.time >= latest) {
      return `${base}，${t('settings.overtimePushDone', '已於 {{time}} 推播', { time: last.time })}`;
    }
    if (settings.autoPushEnabled && latest <= settings.autoPushTime) {
      return `${base}，${t('settings.overtimePushCovered', '由 {{time}} 下班推播一併通報', { time: settings.autoPushTime })}`;
    }
    return `${base}，${t('settings.overtimePushPlanned', '預計 {{time}} 推播', { time: latest })}`;
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleAddHoliday = (e) => {
    e.preventDefault();
    if (!holidayForm.date) {
      setHolidayError(t('errors.required'));
      return;
    }
    holidayMutation.mutate(holidayForm);
  };

  const yearOptions = [-1, 0, 1].map((offset) => new Date().getFullYear() + offset);

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900">{t('settings.pushTitle', '推播時間設定')}</h1>

      {/* Work hours + auto push */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.workHoursAndPush', '收工時間與自動推播')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label required>{t('settings.standardExitTime', '標準收工時間')}</Label>
              <Input
                type="time"
                value={form.standardExitTime}
                onChange={(e) => updateForm('standardExitTime', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.standardExitTimeHint', '收工時間晚於此時間即為加班工項，需於備註填寫原因')}
              </p>
            </div>
            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="font-medium text-gray-700">{t('settings.rulesTitle', '判定規則')}</p>
              <p>• {t('settings.ruleOvertime', '加班：修改收工時間晚於標準收工時間，並於備註填寫原因')}</p>
              <p>• {t('settings.ruleNoWork', '未出工：出工及收工時間欄位空白，並於備註填寫原因')}</p>
            </div>
          </div>

          <div className="border-t pt-6 space-y-4">
            <div>
              <p className="font-medium text-gray-700">{t('settings.autoPush', '每日自動推播 LINE')}</p>
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.autoPushHint', '於下列時段自動發送當日出工概況（含 Excel 下載連結）至 LINE 群組；當日無通報則不發送')}
              </p>
            </div>

            {[
              {
                key: 'onDuty',
                label: t('settings.onDutyPush', '上班推播'),
                enabledField: 'onDutyPushEnabled',
                timeField: 'onDutyPushTime',
                last: settings?.onDutyPushLastDate,
              },
              {
                key: 'offDuty',
                label: t('settings.offDutyPush', '下班推播'),
                enabledField: 'autoPushEnabled',
                timeField: 'autoPushTime',
                last: settings?.autoPushLastDate,
              },
            ].map((slot) => (
              <div key={slot.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-end rounded-lg border border-gray-200 p-3">
                <div>
                  <Label>{slot.label}</Label>
                  <Select
                    value={form[slot.enabledField] ? 'true' : 'false'}
                    onChange={(e) => updateForm(slot.enabledField, e.target.value === 'true')}
                  >
                    <option value="false">{t('settings.disabled', '停用')}</option>
                    <option value="true">{t('settings.enabled', '啟用')}</option>
                  </Select>
                </div>
                <div>
                  <Label>{t('settings.autoPushTime', '推播時間')}</Label>
                  <Input
                    type="time"
                    value={form[slot.timeField]}
                    onChange={(e) => updateForm(slot.timeField, e.target.value)}
                    disabled={!form[slot.enabledField]}
                  />
                </div>
                <p className="text-xs text-gray-500 md:pb-3">
                  {slot.last
                    ? `${t('settings.autoPushLast', '上次自動推播')}：${formatDateShort(slot.last, locale)}`
                    : ''}
                </p>
              </div>
            ))}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-start rounded-lg border border-gray-200 p-3">
              <div>
                <Label>{t('settings.overtimePush', '加班結束推播')}</Label>
                <Select
                  value={form.overtimePushEnabled ? 'true' : 'false'}
                  onChange={(e) => updateForm('overtimePushEnabled', e.target.value === 'true')}
                >
                  <option value="false">{t('settings.disabled', '停用')}</option>
                  <option value="true">{t('settings.enabled', '啟用')}</option>
                </Select>
              </div>
              <div className="md:col-span-2 text-sm text-gray-600 space-y-1">
                <p>
                  {t('settings.overtimePushHint', '推播時間為當日加班工項中最晚的退場時間；若之後有更晚的加班申請，將延後至最晚時間推播，已推播後新增更晚的加班會再推播一次。')}
                </p>
                <p className="font-medium text-gray-800">{overtimeStatus()}</p>
                {settings?.overtimePushLast && (
                  <p className="text-xs text-gray-500">
                    {t('settings.autoPushLast', '上次自動推播')}：{formatDateShort(settings.overtimePushLast.date, locale)} {settings.overtimePushLast.time}
                  </p>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {t('settings.autoPushGraceHint', '系統若於排定時間未運作（如重新啟動）超過 30 分鐘，該時段將略過不補發。加班結束時間早於或等於下班推播時間時，由下班推播一併通報。')}
            </p>
          </div>

          {/* Configuration status */}
          <div className="space-y-2">
            {[
              { ok: settings?.lineConfigured, label: t('settings.lineStatus', 'LINE 群組設定 (.env LINE_GROUP_ID / LINE_CHANNEL_ACCESS_TOKEN)') },
              { ok: settings?.publicUrlConfigured, label: t('settings.publicUrlStatus', 'Excel 下載連結網址 (.env PUBLIC_URL)') },
            ].map((item) => (
              <p key={item.label} className={`flex items-center gap-2 text-sm ${item.ok ? 'text-green-700' : 'text-amber-700'}`}>
                {item.ok ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {item.label}：{item.ok ? t('settings.configured', '已設定') : t('settings.notConfigured', '未設定')}
              </p>
            ))}
          </div>
        </CardBody>
        <CardFooter>
          <div className="flex items-center justify-end gap-3">
            {saved && <span className="text-sm text-green-600">{t('settings.saved', '已儲存')}</span>}
            {saveMutation.isError && <span className="text-sm text-red-600">{t('errors.generic', '操作失敗')}</span>}
            <Button onClick={() => saveMutation.mutate(form)} isLoading={saveMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Holiday calendar */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>{t('settings.holidayCalendar', '假日行事曆')}</CardTitle>
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-32">
              {yearOptions.map((y) => (
                <option key={y} value={y}>{locale === 'zh-TW' ? `${y - 1911} 年` : y}</option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-gray-500">
            {t('settings.holidayHint', '週六、週日自動判定為假日。請在此加入國定假日；若週末需上班（補班日）請設為「補班日」。')}
          </p>

          <form onSubmit={handleAddHoliday} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label required>{t('settings.holidayDate', '日期')}</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('settings.holidayName', '名稱')}</Label>
              <Input
                value={holidayForm.name}
                onChange={(e) => setHolidayForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('settings.holidayNamePlaceholder', '例：國慶日')}
              />
            </div>
            <div>
              <Label>{t('settings.holidayType', '類型')}</Label>
              <Select
                value={holidayForm.type}
                onChange={(e) => setHolidayForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                <option value="holiday">{t('settings.typeHoliday', '國定假日')}</option>
                <option value="workday">{t('settings.typeWorkday', '補班日')}</option>
              </Select>
            </div>
            <Button type="submit" isLoading={holidayMutation.isPending}>
              <Plus className="w-4 h-4 mr-1" />
              {t('common.create')}
            </Button>
          </form>
          {holidayError && <p className="text-sm text-red-600">{holidayError}</p>}
        </CardBody>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.holidayDate', '日期')}</TableHead>
                <TableHead>{t('settings.holidayName', '名稱')}</TableHead>
                <TableHead>{t('settings.holidayType', '類型')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidaysLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">…</TableCell>
                </TableRow>
              ) : holidayList?.length > 0 ? (
                holidayList.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateShort(h.date, locale)}（{getDayOfWeek(h.date, locale)}）
                    </TableCell>
                    <TableCell>{h.name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={h.type === 'holiday' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                        {h.type === 'holiday' ? t('settings.typeHoliday', '國定假日') : t('settings.typeWorkday', '補班日')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteHolidayMutation.mutate(h.id)}
                          disabled={deleteHolidayMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}
